import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import SessionEndDialog from "@memorang/ui/components/StudySession/SessionEndDialog";
import FormativeStudySessionAppbar from "@memorang/ui/components/StudySession/StudySessionAppbar/FormativeStudySessionAppbar";
import FlightCalculatorDialog from "components/FlightCalculator/FlightCalculatorDialog";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import useFormativeItemTimer from "../hooks/useFormativeItemTimer";
import useSessionAppBarHandlers from "../hooks/useSessionAppBarHandlers";
import useSessionAppBarState from "../hooks/useSessionAppBarState";

type Props = {
	showStartDialog?: boolean;
	showAudioPlaybackTestDialog?: boolean;
};

const FormativeAppBarContainer = ({
	showStartDialog,
	showAudioPlaybackTestDialog,
}: Props) => {
	const [showFlightCalculator, setShowFlightCalculator] = useState(false);
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);
	const [showBrowserBackWarning, setShowBrowserBackWarning] = useState(false);
	const [hasFocus, setHasFocus] = useState(false);

	useFocusEffect(
		useCallback(() => {
			setHasFocus(true);
			return () => setHasFocus(false);
		}, []),
	);

	const s = useSessionAppBarState();

	const currentProgress = useSessionStore((state) => state.currentProgress);
	const { currentItem } = useCurrentSession();
	const isAnswered = useSessionStore((state) =>
		Boolean(state.itemStates?.get(currentItem?.id ?? 0)?.answered),
	);
	const isTimerPausingDialogOpen = Boolean(
		showStartDialog || showAudioPlaybackTestDialog,
	);

	const { showTimer, isOverrun, formattedTime } = useFormativeItemTimer(
		isTimerPausingDialogOpen,
	);

	const toggeShowEndSessionDialog = useCallback(() => {
		if (s.inReviewMode) {
			router.back();
			return;
		}
		setShowEndSessionDialog((prev) => !prev);
	}, [s.inReviewMode]);

	const handlers = useSessionAppBarHandlers({
		hasFocus,
		isOlxSession: s.isOlxSession,
		isTimeExpired: s.isTimeExpired,
		isSummativeTest: false,
		inReviewMode: s.inReviewMode,
		hasAnsweredAtLeastOneItem: s.hasAnsweredAtLeastOneItem,
		sessionId: s.sessionId,
		sessionNumericId: s.sessionNumericId,
		projectId: s.projectId,
		organizationId: s.organizationId,
		examId: s.examId,
		examName: s.examName,
		currentItemId: s.currentItemId,
		totalTime: s.totalTime,
		isMarked: s.isMarked,
		showDialog: isTimerPausingDialogOpen,
		numSections: s.numSections,
		numTotalItems: s.numTotalItems,
		sessionSubType: s.sessionSubType,
		diagnosticLabel: s.diagnosticLabel,
		onToggleEndSessionDialog: toggeShowEndSessionDialog,
		onToggleBrowserBackWarning: setShowBrowserBackWarning,
		onToggleFlightCalculator: setShowFlightCalculator,
	});

	return (
		<>
			<FormativeStudySessionAppbar
				handleClickRightCta={handlers.handleClickRightCta}
				currentProgress={currentProgress}
				isAnswered={isAnswered}
				timerLabel={showTimer ? formattedTime : undefined}
				isTimerOverrun={isOverrun}
			/>
			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggeShowEndSessionDialog}
					handleAction={handlers.handleNavigation}
					hasAnsweredAtLeastOneItem={s.hasAnsweredAtLeastOneItem}
					isDiagnostic={s.isDiagnostic}
					numItemsLeft={s.numItemsLeft}
				/>
			)}
			{showFlightCalculator && (
				<FlightCalculatorDialog
					dialogVisible={showFlightCalculator}
					hideDialog={() => setShowFlightCalculator(false)}
				/>
			)}
			{showBrowserBackWarning && handlers.exitDialogCopy && (
				<ConfirmDialog
					visible={showBrowserBackWarning}
					title={handlers.exitDialogCopy.title}
					icon="alert-circle-outline"
					body={handlers.exitDialogCopy.body}
					dismissCallback={handlers.handleConfirmExit}
					dismissCta={handlers.exitDialogCopy.dismissCta}
					confirmCallback={handlers.handleCancelExit}
					confirmCta={handlers.exitDialogCopy.confirmCta}
					maxWidth="xs"
				/>
			)}
		</>
	);
};

export default FormativeAppBarContainer;
